// P14 — Roteamento centralizado de erros 402 (quota_exceeded).
//
// Como funciona:
// - O interceptor do axios chama `handleQuotaError(payload)` ao receber 402.
// - Este módulo decide se abre um modal "bloqueado" (tokens diários/mensais),
//   um modal "pedir mais" (agentes/arquivos/tamanho) ou só um toast curto
//   (modelo não permitido — não é correção self-service).
// - Usa um event bus (`window` custom event) pra renderizar UI: o componente
//   `QuotaErrorHost` (montado no AppLayout) escuta e mostra a modal certa.
// - Singleton guard previne empilhar modais quando user dispara 3 mensagens
//   seguidas que estouram cota.
//
// O caller NÃO precisa fazer nada — apenas catch + parar fluxo. O interceptor
// já chamou o handler antes de re-rejeitar.

import { QuotaErrorResponse } from '@/types/agents';

export type QuotaErrorScope =
  | 'tokens_daily'
  | 'tokens_monthly'
  | 'model_not_allowed'
  | 'agents'
  | 'files_per_agent'
  | 'file_size';

/** Evento custom disparado quando algum 402 deve abrir modal. */
export const QUOTA_ERROR_EVENT = 'quota:error';

export interface QuotaErrorEventDetail {
  /** Tipo de UI a renderizar — derivado do scope do backend. */
  kind: 'blocked' | 'request' | 'toast';
  payload: QuotaErrorResponse;
}

let _activeKind: QuotaErrorEventDetail['kind'] | null = null;

function classifyKind(scope: string): QuotaErrorEventDetail['kind'] {
  if (scope === 'tokens_daily' || scope === 'tokens_monthly') return 'blocked';
  if (scope === 'model_not_allowed') return 'toast';
  return 'request'; // agents, files_per_agent, file_size
}

/** Chamado pelo interceptor do axios. Singleton: 2º 402 simultâneo é ignorado. */
export function handleQuotaError(payload: QuotaErrorResponse): void {
  if (typeof window === 'undefined') return;
  const kind = classifyKind(payload.scope);
  // Singleton só pra modais — toast pode acumular (cada um some sozinho).
  if (kind !== 'toast' && _activeKind) return;
  if (kind !== 'toast') _activeKind = kind;

  const detail: QuotaErrorEventDetail = { kind, payload };
  window.dispatchEvent(new CustomEvent<QuotaErrorEventDetail>(QUOTA_ERROR_EVENT, { detail }));
}

/** Chamado pelo QuotaErrorHost quando o user fecha o modal. */
export function clearActiveQuotaModal(): void {
  _activeKind = null;
}

/** Helper só pra testes — reseta o singleton entre runs. */
export function _resetQuotaErrorState(): void {
  _activeKind = null;
}
